import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { trackPageView } from "@/lib/analytics";
import LiveChat from "@/components/storefront/LiveChat";
import Index from "./pages/Index";
import { AuthProvider } from "./providers/AuthProvider";
import { CartProvider } from "./providers/CartProvider";
import PWA from "./components/PWA";
import CookieBanner from "./components/CookieBanner";
import AntiGravity from "./components/storefront/AntiGravity";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";

// Only the homepage loads eagerly (it's the most common landing page —
// lazy-loading it would add a network round trip before the most-visited
// page can even start rendering). Everything else, INCLUDING THE ENTIRE
// ADMIN PANEL, loads on demand — previously every storefront visitor
// downloaded the full admin Products editor (1800+ lines), order
// management, courier integration, etc. even though they'd never use any
// of it. This is the single biggest lever for mobile performance here.
const NotFound          = lazy(() => import("./pages/NotFound"));
const ProductsCatalogue = lazy(() => import("./pages/ProductsCatalogue"));
const BestSellers       = lazy(() => import("./pages/BestSellers"));
const Policies          = lazy(() => import("./pages/Policies"));
const Contact           = lazy(() => import("./pages/Contact"));
const TrackOrder        = lazy(() => import("./pages/TrackOrder"));
const ProductDetail     = lazy(() => import("./pages/ProductDetail"));
const GiftCards         = lazy(() => import("./pages/GiftCards"));
const AdminLayout       = lazy(() => import("./components/admin/AdminLayout"));
const AdminLogin        = lazy(() => import("./pages/admin/Login"));
const Orders            = lazy(() => import("./pages/admin/Orders"));
const AdminDashboard    = lazy(() => import("./pages/admin/Dashboard"));
const AdminSettings     = lazy(() => import("./pages/admin/Settings"));
const AdminProducts     = lazy(() => import("./pages/admin/Products"));
const AdminUsers        = lazy(() => import("./pages/admin/Users"));
const BostaCourier      = lazy(() => import("./pages/admin/BostaCourier"));
const AdminPromos       = lazy(() => import("./pages/admin/AdminPromos"));
const AdminReviews      = lazy(() => import("./pages/admin/Reviews"));

// Minimal, near-instant fallback — only shown for the brief moment a lazy
// chunk is downloading (typically tens of milliseconds on a real
// connection, since each chunk is small once split apart like this).
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 5, gcTime: 1000 * 60 * 60 } },
});

// RouterContent: INSIDE BrowserRouter — useLocation() is safe here
const RouterContent = () => {
  const location = useLocation();
  const { t } = useTranslation();
  useEffect(() => {
    const titles: Record<string, string> = {
      "/": "ZIKO Marketplace — Global Innovation, Egyptian Pulse",
      "/products": "All Products | ZIKO Marketplace",
      "/best-sellers": "Best Sellers | ZIKO Marketplace",
      "/gift-cards": "Gift Cards | ZIKO Marketplace",
      "/about": "About Us | ZIKO Marketplace",
      "/contact": "Contact Us | ZIKO Marketplace",
      "/support": "Support | ZIKO Marketplace",
      "/faq": "FAQs | ZIKO Marketplace",
      "/collection/gift-ready": "Gift Ready Collection | ZIKO Marketplace",
      "/collection/essentials": "Essentials Collection | ZIKO Marketplace",
      "/collection/trending": "Trending Now | ZIKO Marketplace",
      "/shipping": "Shipping & Delivery | ZIKO Marketplace",
      "/returns": "Returns & Refunds | ZIKO Marketplace",
      "/terms": "Policies | ZIKO Marketplace",
      "/policies": "Policies | ZIKO Marketplace",
      "/track-order": "Track Order | ZIKO Marketplace",
      "/admin": "Admin Dashboard | ZIKO",
      "/admin/products": "Products | ZIKO Admin",
      "/admin/orders": "Orders | ZIKO Admin",
      "/admin/users": "Users | ZIKO Admin",
      "/admin/settings": "Settings | ZIKO Admin",
    };
    const path = location.pathname;
    if (path.startsWith("/category/")) {
      const cat = decodeURIComponent(path.replace("/category/", ""));
      document.title = `${cat} | ZIKO Marketplace`;
    } else {
      document.title = titles[path] || "ZIKO Marketplace";
    }
    trackPageView(path, document.title);
  }, [location.pathname]);

  return (
    <AuthProvider>
      <CartProvider>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/products" element={<ProductsCatalogue catalogueKey="all" />} />
          <Route path="/category/:categoryName" element={<ProductsCatalogue />} />
          <Route path="/collection/essentials" element={<ProductsCatalogue filterTag="Essentials" collectionTitle={t('home.essentials')} collectionEmoji="⚡" collectionDesc={t('home.essentialsDesc')} />} />
          <Route path="/collection/trending" element={<ProductsCatalogue filterTag="Trending Now" collectionTitle={t('home.trendingNow')} collectionEmoji="🔥" collectionDesc={t('home.trendingDesc')} />} />
          <Route path="/collection/gift-ready" element={<ProductsCatalogue filterTag="Gift Ready" collectionTitle={t('home.giftReady')} collectionEmoji="🎁" collectionDesc={t('home.giftDesc')} />} />
          <Route path="/best-sellers" element={<BestSellers />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/ar/product/:id" element={<ProductDetail forceLang="ar" />} />
          <Route path="/gift-cards" element={<GiftCards />} />
          <Route path="/support" element={<Contact />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/about" element={<Contact />} />
          <Route path="/faq" element={<Policies />} />
          <Route path="/shipping" element={<Policies />} />
          <Route path="/returns" element={<Policies />} />
          <Route path="/privacy" element={<Policies />} />
          <Route path="/terms" element={<Policies />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="orders" element={<Orders />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="bosta" element={<BostaCourier />} />
            <Route path="promos" element={<AdminPromos />} />
            <Route path="reviews" element={<AdminReviews />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </CartProvider>
    </AuthProvider>
  );
};

// AppContent: OUTSIDE BrowserRouter — handles RTL/language only
const AppContent = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    const dir = i18n.language === "ar" ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
    document.body.style.fontFamily = i18n.language === "ar"
      ? "'Cairo', 'Inter', sans-serif"
      : "'Inter', sans-serif";
  }, [i18n.language]);

  return (
    <ErrorBoundary>
      <Toaster />
      <Sonner />
      <PWA />
      <CookieBanner />
      <AntiGravity />
      <LiveChat />
      <BrowserRouter>
        <RouterContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AppContent />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;

